import React, { useEffect }  from 'react';
import styled from 'styled-components';

// Smart Office Nav uses 1000 we want this catastrophic error page to cover up a potentially broken nav by default
const defaultZIndex = 1100;

const cdnBaseUrl = 'https://cdn.evolutionpayroll.com/error-page-assets';

export interface Error {
    message?: string;
    displayMessage?: string;
    statusCode?: number;
    stack?: any;
}

export interface ErrorPageLink {
    label: string;
    url?: string;
    callback?: () => void;
}

export interface ErrorPage {
    error?: Error;
    helpfulLinks?: ErrorPageLink[],
    zIndex?: number;
    showDeveloperMessage?: boolean;
}

const notCurrentPage = (link: ErrorPageLink) => {
    return window.location.href !== link.url;
}

const hasMoreInfo = (error: Error) => {
    return error.stack || Object.keys(error).length;
};

const handleCallback = (e: any, link: ErrorPageLink) => {
    if (link.callback) {
        e.preventDefault();
        link.callback();
    }
}

/**
 * The `ErrorPage` component should be used when a catastrophic error prevent an application from continuing. As such, it is
 * expected that it will be rendered from an application's global error handler. It automatically sizes and positions itself
 * to cover the entire browser viewport. This component is responsive and adapts to Phone, Tablet and Desktop viewports.
 *
 * * The `error` prop should contain an error object to display.
 *
 * * The `helpfulLinks` prop is an array of links to render.
 *
 * * If `showDeveloperMessage` prop is set to `true`, then additional details such as a stack trace will be displayed.
 *
 * * The `zIndex` prop allows overriding the default z-index if it is not high enough to cover any application content that has already rendered.
 */
export const ErrorPage = ({ error = {}, helpfulLinks = [], zIndex = defaultZIndex, showDeveloperMessage = false }: ErrorPage) => {

    useEffect(() => {
        const parallax = new Parallax();
        parallax.init();
        return parallax.destroy;
    }, []);

    let message = error.displayMessage || error.message || '';
    const filteredLinks = helpfulLinks.filter(notCurrentPage);
    const hasLinks = Boolean(filteredLinks.length);

    if (!message && !hasLinks){
        message = 'Please contact your administrator for assistance.';
    }

    return (
        <ErrorPageWrapper style={{ zIndex }}>
            <div className="parallax">
                <div className="parallax-layer parallax-layer__1" data-parallax-disallow="y" data-parallax-deep="1000" />
                <div className="parallax-layer parallax-layer__2" data-parallax-deep="300" />
                <div className="parallax-layer parallax-layer__3" data-parallax-deep="600" />
            </div>

            <div className="grid-container">
                <div className="left">
                    <h1>Oops, something went wrong.</h1>
                    <p>{message}</p>
                    { hasLinks &&
                        <>
                            <p>You might find a link below helpful:</p>
                            <ul>
                                { filteredLinks.map((link, i) => (
                                    <li key={i}><a href={link.url || '#'} onClick={(e) => handleCallback(e, link)}>{link.label}</a></li>
                                ))}
                            </ul>
                        </>
                    }
                </div>
                <div className="right" />
                { showDeveloperMessage && hasMoreInfo(error) &&
                    <pre className='developer-panel'>
                        {error.stack || error.toString()}<br />
                        Other attributes: {JSON.stringify(error, undefined, 2)}
                    </pre>
                }
            </div>

            <div className="top-ladder">
                <img src={`${cdnBaseUrl}/top-ladder.png`} alt="ladder going up"/>
            </div>

            <div className="clouds"></div>
            <div className="woman-and-ladder"></div>
        </ErrorPageWrapper>
    );
};

export default ErrorPage;

const ErrorPageWrapper = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    height: 100vh;
    width: 100%;
    display: block;

    background: #0d4468;
    background: -moz-linear-gradient(top, #0d4468 0%, #066783 20%, #e5e5e5 100%);
    background: -webkit-linear-gradient(top, #0d4468 0%, #066783 20%, #e5e5e5 100%);
    background: linear-gradient(to bottom, #0d4468 0%, #066783 20%, #e5e5e5 100%);
    filter: progid:DXImageTransform.Microsoft.gradient( startColorstr='#0d4468', endColorstr='#e5e5e5',GradientType=0 );
    font-family: 'Roboto', sans-serif;

    * {
        margin: 0;
        padding: 0;
        font-size: 16px;
    }
    div.clouds {
        position: absolute;
        bottom: 0;
        left: 0;
        height: 45%;
        width: 100%;
        background-image: url(${cdnBaseUrl}/clouds-front.png);
        background-size: 200% 100%;
        background-position: bottom center;
        background-repeat: no-repeat;
    }
    .grid-container {
        padding-left: 20px;
        max-width: 1000px;
        margin: 4rem auto 0;
        position: relative;
        z-index: 200;
    }
    .grid-container .left {
        display: inline-block;
    }
    .grid-container .left h1 {
        font-size: 36px;
        margin-bottom: 1.5rem;
        color: #E7E7E7;
        font-weight: normal;
        text-shadow: 2px 2px 18px rgba(0, 0, 0, 0.35);
    }
    .grid-container .left p {
        min-height: 20px;
        color: #fff;
        font-size: 1rem;
        margin-bottom: 1.5rem;
        font-weight: 300;
    }
    .grid-container .left ul {
        list-style-type: none;
    }
    .grid-container .left ul li a {
        color: #005279;
        text-decoration: none;
        font-size: 18px;
        display: block;
    }
    .grid-container .left ul li a:hover {
        opacity: .75;
    }
    .woman-and-ladder {
        background-image: url("${cdnBaseUrl}/woman-and-ladder2.png");
        background-size: contain;
        background-position: top left;
        position: absolute;
        background-repeat: no-repeat;
        width: 140px;
        height: 48%;
        bottom: 0;
        left: 60%;
    }
    .top-ladder {
        text-align: center;
        width: 50px;
        position: absolute;
        top: 0;
        right: 5%;
    }
    .top-ladder img {
        max-width: 100%;
    }
    .parallax {
        width: 100vw;
        height: 100vh;
        overflow: hidden;
        position: absolute;
        z-index: 0;
    }
    .parallax-layer {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-position: center;
        background-repeat: no-repeat;
        background-size: contain;
    }
    .parallax-layer__1 {
        background-image: url("${cdnBaseUrl}/small-stars.png");
        background-size: cover;
    }
    .parallax-layer__2 {
        background-image: url("${cdnBaseUrl}/big-stars.png");
    }
    .parallax-layer__3 {
        background-image: url("${cdnBaseUrl}/clouds-back.png");
        background-position: bottom;
        background-repeat: repeat-x;
    }
    .developer-panel {
        position: fixed;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 150px;
        background-color: lightgray;
        opacity: 0.8;
        padding: 1rem;
    }

    @media screen and (min-width: 376px) {
        .grid-container {
            margin-top: 5rem;
        }
        .grid-container .left {
            width: 70%;
        }
        .grid-container .left h1 {
            font-size: 65px;
        }
        .grid-container .left p {
            width: 75%;
            font-size: 1.3rem;
        }
        .grid-container .left ul li a {
            font-size: 22px;
        }
        .top-ladder {
            width: 150px;
            top: 0;
            right: 5%;
            height: 30%;
        }
        .woman-and-ladder {
            width: 160px;
            height: 75%;
            left: 56%;
            bottom: 0;
        }
        div.clouds {
            height: 35%;
            background-size: cover;
        }
    }

    @media screen and (min-width: 769px) {
        .grid-container {
            padding-left: 0;
        }
        .top-ladder {
            right: 20%;
        }
    }
    .docs-story & {
        height: 100%;
        width: 100%;
        z-index: 0;
        position: static;

        .parallax {
            height: 100%;
            width: 100%;
            z-index: 0;
        }
        .developer-panel {
            position: absolute;
            top: 100%;
            bottom: auto;
        }
    }
`;

function Parallax({ wrapper = 'body', layers = '.parallax-layer', deep = 'data-parallax-deep' }: { wrapper?: string, layers?: string, deep?: string } = {}): void {
    this.nameSpaces = {
        wrapper,
        layers,
        deep
    };

    const unsubscribeFunctions = [];
    this.init = () => {
        var self = this,
            parallaxWrappers = document.querySelectorAll(this.nameSpaces.wrapper);
        for (var i = 0; i < parallaxWrappers.length; i++) {
            (function (i) {
                const onMouseMove = (e) => {
                    var x = e.clientX,
                        y = e.clientY,
                        layers = parallaxWrappers[i].querySelectorAll(self.nameSpaces.layers);
                    for (var j = 0; j < layers.length; j++) {
                        (function (j) {
                            var deep = layers[j].getAttribute(self.nameSpaces.deep),
                                disallow = layers[j].getAttribute('data-parallax-disallow'),
                                itemX = (disallow && disallow === 'x') ? 0 : x / deep,
                                itemY = (disallow && disallow === 'y') ? 0 : y / deep;
                            if (disallow && disallow === 'both') return;
                            layers[j].style.transform = 'translateX(' + itemX + '%) translateY(' + itemY + '%)';
                        })(j);
                    }
                };
                parallaxWrappers[i].addEventListener('mousemove', onMouseMove);
                unsubscribeFunctions.push(() => parallaxWrappers[i].removeEventListener('mousemove', onMouseMove));
            })(i);
        }
    };

    this.destroy = () => unsubscribeFunctions.forEach(unsub => unsub());

    return this;
}
